import { useDefaultNodeNameInfo } from "@/hooks/shared/useDefaultNodeName";
import { QueryConfig } from "@/lib/react-query";
import { ScenarioBuilderClient } from "@/pages/scenario-builder/api/client/scenarioClient";
import { useScenarioBuilderClient } from "@/pages/scenario-builder/api/hooks/useScenarioBuilderClient";
import { useQuery } from "@tanstack/react-query";
import { getExtendedActionPlanDetails } from "./getActionPlan";
import { ActionPlanSummary } from "@mutiny-group/sbo-openapi-client";

type GetActionPlansOptions = {
  scenarioBuilderClient: ScenarioBuilderClient;
};

const getActionPlans = ({ scenarioBuilderClient }: GetActionPlansOptions) => {
  return scenarioBuilderClient.actionPlans.getActionPlans();
};

type UseGetActionPlansOptions = {
  config?: QueryConfig<typeof getActionPlans>;
};

export const useActionPlans = ({ config }: UseGetActionPlansOptions = {}) => {
  const scenarioBuilderClient = useScenarioBuilderClient();
  const { mapDefaultNodeName } = useDefaultNodeNameInfo();

  return useQuery({
    ...config,
    queryKey: ["action-plans", scenarioBuilderClient],
    queryFn: () => getActionPlans({ scenarioBuilderClient }),
    select: (data) => {
      const { actionPlans } = data;
      return {
        actionPlans: actionPlans.map((actionPlan) => {
          const { spendPercentage, revenuePercentage } = getExtendedActionPlanDetails(actionPlan);
          return {
            ...actionPlan,
            scopeBrandForDisplay: mapDefaultNodeName(actionPlan.scopeBrand ?? "default"),
            spendPercentage,
            revenuePercentage,
          };
        }),
        shouldShowNewRecommendations: shouldShowNewRecommendations(actionPlans),
      };
    },
  });
};

// Show the recommendations banner if there is any action plan with actual or forecast revenue or spend greater than 0
const shouldShowNewRecommendations = (actionPlans: ActionPlanSummary[]) => {
  return actionPlans.some(
    ({
      revenueForecastToDate,
      revenueActualToDate,
      spendPlannedToDate,
      spendActualToDate,
      unitsForecastToDate,
      unitsActualToDate,
    }) =>
      [
        revenueForecastToDate,
        revenueActualToDate,
        spendPlannedToDate,
        spendActualToDate,
        unitsForecastToDate,
        unitsActualToDate,
      ].some((value) => value > 0)
  );
};
